import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Rss, RefreshCw, ExternalLink, ImageOff, ImageIcon } from "lucide-react";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { decodeHtmlEntities } from "@/lib/htmlEntities";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BlogArticle {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  article_url: string;
  published_at: string | null;
  created_at: string;
}

const AdminBlog = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLoading: authLoading, isAdmin } = useAuth();
  
  const [feedUrl, setFeedUrl] = useState("");
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [refreshingImages, setRefreshingImages] = useState(false);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'it': return it;
      case 'es': return es;
      default: return enUS;
    }
  };

  useEffect(() => {
    if (!authLoading && !isAdmin()) {
      navigate("/");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load blog settings
      const { data: settings } = await supabase
        .from('blog_settings')
        .select('*')
        .limit(1);
      
      if (settings && settings.length > 0) {
        setFeedUrl(settings[0].feed_url || "");
        setLastFetched(settings[0].last_fetched_at);
      }
      
      // Load articles
      const { data: articlesData } = await supabase
        .from('blog_articles')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(6);
      
      if (articlesData) {
        setArticles(articlesData);
      }
    } catch (error) {
      console.error('Error loading blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveFeedUrl = async () => {
    if (!feedUrl.trim()) {
      toast.error(t("admin.blog.feedUrlRequired", "URL del feed richiesto"));
      return;
    }

    try {
      setSaving(true);
      
      // Check if settings exist
      const { data: existing } = await supabase
        .from('blog_settings')
        .select('id')
        .limit(1);
      
      if (existing && existing.length > 0) {
        // Update existing
        await supabase
          .from('blog_settings')
          .update({ feed_url: feedUrl.trim() })
          .eq('id', existing[0].id);
      } else {
        // Insert new
        await supabase
          .from('blog_settings')
          .insert({ feed_url: feedUrl.trim() });
      }
      
      toast.success(t("admin.blog.saveSuccess"));
    } catch (error) {
      console.error('Error saving feed URL:', error);
      toast.error(t("admin.blog.saveError", "Errore nel salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const fetchArticles = async () => {
    if (!feedUrl.trim()) {
      toast.error(t("admin.blog.feedUrlRequired", "URL del feed richiesto"));
      return;
    }

    try {
      setFetching(true);
      
      const response = await supabase.functions.invoke('fetch-blog-feed', {
        body: { feedUrl: feedUrl.trim() },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to fetch articles');
      }

      toast.success(t("admin.blog.fetchSuccess"));
      
      // Reload data
      await loadData();
    } catch (error: any) {
      console.error('Error fetching articles:', error);
      toast.error(t("admin.blog.fetchError"));
    } finally {
      setFetching(false);
    }
  };

  const refreshMissingImages = async () => {
    try {
      setRefreshingImages(true);
      
      const response = await supabase.functions.invoke('fetch-blog-feed', {
        body: { refreshMissingImages: true },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to refresh images');
      }

      const { updatedCount, totalWithoutImages } = response.data;
      
      if (updatedCount > 0) {
        toast.success(t("admin.blog.imagesUpdated", { count: updatedCount }));
      } else if (totalWithoutImages === 0) {
        toast.info(t("admin.blog.noMissingImages", "Tutti gli articoli hanno già un'immagine"));
      } else {
        toast.info(t("admin.blog.noImagesFound", "Nessuna immagine trovata nelle pagine degli articoli"));
      }
      
      // Reload data
      await loadData();
    } catch (error: any) {
      console.error('Error refreshing images:', error);
      toast.error(t("admin.blog.refreshImagesError", "Errore nell'aggiornamento immagini"));
    } finally {
      setRefreshingImages(false);
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/settings">{t("admin.settings.title")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.blog.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/settings")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.blog.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Rss className="w-10 h-10 text-primary" />
              {t("admin.blog.title")}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t("admin.blog.subtitle")}
            </p>
          </div>
        </div>

        {/* Feed URL Section */}
        <BrutalCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary border-[3px] border-dark flex items-center justify-center">
              <Rss className="w-5 h-5 text-dark" />
            </div>
            <h2 className="font-heading font-bold text-xl">
              {t("admin.blog.feedUrl")}
            </h2>
          </div>

            <div className="space-y-3">
              {/* Row 1: Input + Save */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <BrutalInput
                    value={feedUrl}
                    onChange={(e) => setFeedUrl(e.target.value)}
                    placeholder={t("admin.blog.feedUrlPlaceholder")}
                    className="w-full"
                  />
                </div>
                <BrutalButton
                  onClick={saveFeedUrl}
                  disabled={saving}
                  variant="secondary"
                  className="w-full sm:w-auto"
                >
                  {saving ? "..." : t("admin.blog.saveUrl")}
                </BrutalButton>
              </div>

              {/* Row 2: Fetch + Refresh */}
              <div className="flex flex-col sm:flex-row gap-3">
                <BrutalButton
                  onClick={fetchArticles}
                  disabled={fetching || !feedUrl.trim()}
                  className="w-full sm:flex-1 lg:flex-none lg:w-auto"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
                  {fetching ? t("admin.blog.fetching") : t("admin.blog.fetchArticles")}
                </BrutalButton>
                <BrutalButton
                  onClick={refreshMissingImages}
                  disabled={refreshingImages}
                  variant="outline"
                  className="w-full sm:flex-1 lg:flex-none lg:w-auto"
                >
                  <ImageIcon className={`w-4 h-4 mr-2 ${refreshingImages ? 'animate-pulse' : ''}`} />
                  {refreshingImages ? t("admin.blog.refreshingImages") : t("admin.blog.refreshMissingImages")}
                </BrutalButton>
              </div>
            </div>

          {lastFetched && (
            <p className="text-sm text-muted-foreground mt-3">
              {t("admin.blog.lastFetched")}: {format(new Date(lastFetched), "PPp", { locale: getDateLocale() })}
            </p>
          )}
        </BrutalCard>

        {/* Articles Section */}
        <div>
          <h2 className="font-heading font-bold text-xl mb-4">
            {t("admin.blog.articlesTitle")} ({articles.length})
          </h2>

          {articles.length === 0 ? (
            <BrutalCard className="p-8 text-center">
              <p className="text-muted-foreground">{t("admin.blog.noArticles")}</p>
            </BrutalCard>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((article) => (
                <BrutalCard key={article.id} className="overflow-hidden">
                  {article.image_url ? (
                    <div className="aspect-video w-full overflow-hidden border-b-[3px] border-dark">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <div className="hidden aspect-video w-full bg-muted flex items-center justify-center">
                        <ImageOff className="w-8 h-8 text-muted-foreground" />
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video w-full bg-muted flex items-center justify-center border-b-[3px] border-dark">
                      <ImageOff className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-lg line-clamp-2 mb-2">
                      {decodeHtmlEntities(article.title)}
                    </h3>
                    
                    {article.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        {decodeHtmlEntities(article.description)}
                      </p>
                    )}

                    {article.published_at && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {format(new Date(article.published_at), "PP", { locale: getDateLocale() })}
                      </p>
                    )}

                    <a
                      href={article.article_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      {t("admin.blog.readMore")}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </BrutalCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlog;
